import React, { useEffect, useState } from "react";
import { api, formatINR } from "@/lib/api";
import { toast } from "sonner";
import { DisclaimerBanner } from "@/components/DisclaimerBanner";
import { useCopy, useLang } from "@/contexts/LanguageContext";

const COPY = {
  en: { h: "Scheme Rule Builder", p: "Configure schemes without code changes.", add: "+ Add scheme", edit: "Edit scheme", new: "New scheme", desc: "Description", save: "Save", cancel: "Cancel", editBtn: "Edit", saved: "Saved", fail: "Save failed",
    fields: ["Scheme name", "Code", "Type", "Nodal department", "Implementing agency", "Benefit type", "Max benefit (₹)", "Collateral rule", "Official source"], cols: ["Name", "Type", "Max benefit", "Version", "Verified"] },
  hi: { h: "योजना नियम निर्माता", p: "बिना कोड बदले योजनाएँ निर्धारित करें।", add: "+ योजना जोड़ें", edit: "योजना संपादित करें", new: "नई योजना", desc: "विवरण", save: "सहेजें", cancel: "रद्द करें", editBtn: "संपादित करें", saved: "सहेजा गया", fail: "सहेजना विफल",
    fields: ["योजना का नाम", "कोड", "प्रकार", "नोडल विभाग", "कार्यान्वयन एजेंसी", "लाभ का प्रकार", "अधिकतम लाभ (₹)", "संपार्श्विक नियम", "आधिकारिक स्रोत"], cols: ["नाम", "प्रकार", "अधिकतम लाभ", "संस्करण", "सत्यापित"] },
};

const emptyScheme = {
  name: "", code: "", type: "Madhya Pradesh", nodal_department: "",
  implementing_agency: "", benefit_type: "", max_benefit_amount: 0,
  eligible_business_types: ["new"], collateral_rule: "", description: "",
  official_source: "", active: true,
};

export default function AdminSchemes() {
  const [schemes, setSchemes] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(emptyScheme);
  const c = useCopy(COPY);
  const { tv } = useLang();

  const load = () => api.get("/schemes").then((r) => setSchemes(r.data)).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const startEdit = (s) => { setEditing(s.id); setForm({ ...emptyScheme, ...s }); setCreating(true); };
  const cancel = () => { setEditing(null); setCreating(false); setForm(emptyScheme); };
  const save = async () => {
    try {
      if (editing) await api.put(`/admin/schemes/${editing}`, form);
      else await api.post("/admin/schemes", form);
      toast.success(c.saved); cancel(); await load();
    } catch (e) { toast.error(c.fail); }
  };

  return (
    <div className="space-y-6" data-testid="admin-schemes">
      <div className="flex items-baseline justify-between">
        <div><h1 className="text-3xl font-bold text-slate-900 brand-heading">{c.h}</h1><p className="text-slate-600 mt-1 text-sm">{c.p}</p></div>
        <button onClick={() => { setCreating(true); setForm(emptyScheme); setEditing(null); }} data-testid="add-scheme-btn" className="rounded-md bg-slate-900 hover:bg-slate-800 text-white px-4 py-2 text-sm font-semibold">{c.add}</button>
      </div>

      {creating && (
        <div className="p-6 rounded-lg border border-slate-200 bg-white">
          <h2 className="font-semibold text-slate-900 brand-heading">{editing ? c.edit : c.new}</h2>
          <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
            {[
              ["name", "text"], ["code", "text"], ["type", "text"], ["nodal_department", "text"],
              ["implementing_agency", "text"], ["benefit_type", "text"], ["max_benefit_amount", "number"],
              ["collateral_rule", "text"], ["official_source", "text"],
            ].map(([k, t], fi) => (
              <div key={k}>
                <label className="block text-xs text-slate-500">{c.fields[fi]}</label>
                <input type={t} value={form[k] ?? ""} onChange={(e) => setForm({ ...form, [k]: t === "number" ? Number(e.target.value) : e.target.value })} data-testid={`scheme-${k}`} className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1.5" />
              </div>
            ))}
            <div className="md:col-span-3">
              <label className="block text-xs text-slate-500">{c.desc}</label>
              <textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="scheme-desc" className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1.5" />
            </div>
          </div>
          <div className="mt-4 flex gap-2">
            <button onClick={save} data-testid="scheme-save" className="rounded-md bg-slate-900 hover:bg-slate-800 text-white px-5 py-2 text-sm font-semibold">{c.save}</button>
            <button onClick={cancel} className="rounded-md border border-slate-300 px-4 py-2 text-sm" data-testid="scheme-cancel">{c.cancel}</button>
          </div>
        </div>
      )}

      <div className="overflow-x-auto table-scroll rounded-lg border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-widest text-slate-500 border-b border-slate-200 bg-slate-50">
            <tr>{c.cols.map((h) => <th key={h} className="py-3 px-4">{h}</th>)}<th className="py-3 px-4"></th></tr>
          </thead>
          <tbody>
            {schemes.map((s) => (
              <tr key={s.id} className="border-b border-slate-100" data-testid={`sch-${s.id}`}>
                <td className="py-3 px-4 font-medium">{s.name}</td>
                <td className="py-3 px-4">{tv(s.type)}</td>
                <td className="py-3 px-4">{formatINR(s.max_benefit_amount)}</td>
                <td className="py-3 px-4">v{s.version}</td>
                <td className="py-3 px-4 text-xs text-slate-500">{s.last_verified_date?.slice(0, 10)}</td>
                <td className="py-3 px-4"><button onClick={() => startEdit(s)} data-testid={`edit-sch-${s.id}`} className="text-xs font-semibold text-slate-900 hover:text-orange-600">{c.editBtn}</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <DisclaimerBanner compact />
    </div>
  );
}
